import { useEffect, useRef, useState } from 'react';
import { useClickOutside } from '../../lib/useClickOutside';
import type { HeadlinePinPosition } from '../../types/campaign';
import { VariationsListPopover } from './VariationsListPopover';

// P2b-3 §3 / §4 + round-2 polish Fix 1: read-only multi-value cell
// for the role grids (body copy / headlines / descriptions). This
// file owns the inline preview button + click-to-open + fixed-position
// popover anchoring. The popover BODY is delegated to
// <VariationsListPopover> — that component owns the per-card layout,
// "Copy all" header, per-card copy buttons, and the empty state.
//
// Display modes (cell-level truncation contract from spec §3.1 / §4.1):
//   - 'first-line': show only the first line of the first array entry.
//                   Used for body copy.
//   - 'first-three-list': show first 3 items joined with " · ". Used
//                         for headlines + descriptions.
//
// Empty arrays render an em-dash placeholder and the popover doesn't
// open (cell-level early return).

type Props = {
  values: string[] | null | undefined;
  // Pinned Headlines spec §2.3: parallel pin-position array. Only
  // passed for Google headlines; other call sites omit it.
  pins?: Array<HeadlinePinPosition> | null;
  mode: 'first-line' | 'first-three-list';
  ariaLabel: string;
  popoverTitle: string;
  // Optional fixed width for the inline preview to keep the column widths
  // stable as content varies.
  className?: string;
};

export function ReadOnlyArrayCell({
  values,
  pins,
  mode,
  ariaLabel,
  popoverTitle,
  className,
}: Props) {
  // Co-filter values + pins so the popover keeps them aligned after
  // dropping the empty/non-string entries.
  const raw = values ?? [];
  const items: string[] = [];
  const itemPins: HeadlinePinPosition[] = [];
  for (let i = 0; i < raw.length; i++) {
    const v = raw[i];
    if (typeof v !== 'string' || v.length === 0) continue;
    items.push(v);
    itemPins.push(pins ? pins[i] ?? null : null);
  }
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useClickOutside([anchorRef, popoverRef], () => setOpen(false), open);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  // Fixed-position popover anchoring (same pattern as NotesCell so the
  // table's overflow-x doesn't clip it).
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  useEffect(() => {
    if (!open) return;
    const compute = () => {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (!rect) return;
      const popWidth = 440;
      const left = Math.min(rect.left, window.innerWidth - popWidth - 16);
      const top = rect.bottom + 4;
      setPos({ top, left: Math.max(16, left) });
    };
    compute();
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, true);
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute, true);
    };
  }, [open]);

  if (items.length === 0) {
    return <span className="text-slate-300">—</span>;
  }

  const preview =
    mode === 'first-line' ? firstLine(items[0]!) : firstThreeJoined(items);

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={ariaLabel}
        title={items.join('\n')}
        className={
          'text-left text-xs rounded border border-slate-200 px-2 py-1 truncate hover:bg-slate-50 ' +
          (className ?? 'w-48')
        }
      >
        {preview}
        {items.length > (mode === 'first-line' ? 1 : 3) && (
          <span className="ml-1 text-slate-400">+{items.length - (mode === 'first-line' ? 1 : 3)}</span>
        )}
      </button>

      {open && pos && (
        <div
          ref={popoverRef}
          role="dialog"
          aria-label={popoverTitle}
          className="fixed z-40 w-[440px] rounded-md border border-slate-300 bg-white shadow-xl p-3 max-h-[70vh] overflow-y-auto"
          style={{ top: pos.top, left: pos.left }}
        >
          <VariationsListPopover
            title={popoverTitle}
            variations={items}
            pins={itemPins}
          />
        </div>
      )}
    </>
  );
}

function firstLine(text: string): string {
  const nl = text.indexOf('\n');
  return (nl < 0 ? text : text.slice(0, nl)).trim() || text.trim();
}

function firstThreeJoined(items: string[]): string {
  return items.slice(0, 3).map((s) => s.replace(/\n/g, ' ')).join(' · ');
}
